import { Injectable, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Observable, map } from 'rxjs';
import { ApiService } from '../../api.service';
import { CacheableObservable } from '../../cacheable-observable/cacheable-observable.model';
import { UserPermissionsEnum } from '../../core/enums/user-permissions.enum';
import {
  ManagedUserPermissionsForm,
  ManagedUserPermissionsFormValue,
  ManagedUserProfileForm,
  formControlsToPermissionsEnumMap,
} from '../../core/forms/managed-user.form';
import { ManagedUserFilterParamEnum, ManagedUserInterface, ManagedUsersListRowInterface } from '../../core/models/managed-user.model';
import {
  UserPermissionInfoInterface,
  UserPermissionOptionsInterface,
  UserPermissionsInterface,
} from '../../core/models/user-permissions.model';
import { FilterableListServiceInterface } from '../../filterable-list/filterable-list-service.interface';
import { email, noWhitespace, onlyText, validPhoneNumber } from '../../shared/class/custom-validators';

export interface CreateManagedUserInterface {
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
  permissions?: UserPermissionsCreateAndUpdateInterface;
}

export interface UpdateManagedUserInterface extends Omit<CreateManagedUserInterface, 'email'> {
  isActive?: boolean;
}

export interface UserPermissionsCreateAndUpdateInterface {
  granted: UserPermissionsEnum[];
  informationPages: string[];
}

interface CreateAndUpdateUserResponseInterface {
  id: number;
}

@Injectable({
  providedIn: 'root',
})
export class ManagedUsersService implements FilterableListServiceInterface {
  private api = inject(ApiService);

  filterAll(filter: ManagedUserFilterParamEnum): CacheableObservable<ManagedUsersListRowInterface[]> {
    return this.api.get('company-users/', filter);
  }

  create(data: CreateManagedUserInterface): Observable<CreateAndUpdateUserResponseInterface> {
    return this.api.post('company-users/create', data);
  }

  getOne(id: number): Observable<ManagedUserInterface> {
    return this.api
      .get(`company-users/${id}`)
      .noCache()
      .pipe(map(({ data }) => data));
  }

  update(id: number, data: UpdateManagedUserInterface): Observable<CreateAndUpdateUserResponseInterface> {
    return this.api.patch(`company-users/${id}`, data);
  }

  getPermissionOptions(): Observable<UserPermissionOptionsInterface> {
    return this.api
      .get('company-users/permission-options')
      .noCache()
      .pipe(map(({ data }) => data));
  }

  resendPasswordLink(id: number): Observable<void> {
    return this.api.get(`company-users/${id}/password-reset`);
  }

  getUserProfileForm(user?: ManagedUserInterface): ManagedUserProfileForm {
    return new FormGroup({
      email: new FormControl({ value: user?.email ?? null, disabled: !!user }, [Validators.required, email]),
      firstName: new FormControl(user?.firstName ?? null, [noWhitespace, onlyText, Validators.maxLength(50)]),
      lastName: new FormControl(user?.lastName ?? null, [noWhitespace, onlyText, Validators.maxLength(50)]),
      phone: new FormControl(user?.phone ?? null, [validPhoneNumber(), Validators.maxLength(20)]),
    });
  }

  getUserPermissionsForm(
    permissions: UserPermissionsInterface,
    availableInformationPages: UserPermissionInfoInterface[],
  ): ManagedUserPermissionsForm {
    const { granted, deniedInformationPages } = permissions;

    const infoPagesPermissionControls = availableInformationPages.reduce(
      (permissions, pagePermissionInfo) => {
        permissions[pagePermissionInfo.value] = new FormControl(!deniedInformationPages.includes(pagePermissionInfo.value));

        return permissions;
      },
      {} as Record<string, FormControl<boolean>>,
    );

    const basePermissionControls = Object.entries(formControlsToPermissionsEnumMap).reduce(
      (permissions, [controlName, permissionsEnumValue]) => {
        permissions[controlName] = new FormControl(granted.includes(permissionsEnumValue));
        return permissions;
      },
      {} as Record<string, FormControl<boolean>>,
    );

    return new FormGroup({
      ...basePermissionControls,
      ...infoPagesPermissionControls,
    }) as unknown as ManagedUserPermissionsForm;
  }

  formatPermissionsForUpdate(
    permissionsFormValue: ManagedUserPermissionsFormValue,
    permissionOptions: UserPermissionOptionsInterface,
  ): UserPermissionsCreateAndUpdateInterface {
    const permissions: UserPermissionsCreateAndUpdateInterface = {
      granted: [],
      informationPages: [],
    };
    const disabledPermissions = permissionOptions.granted.filter((permission) => permission.disabled).map((permission) => permission.value);
    const disabledInfoPages = permissionOptions.informationPages
      .filter((permission) => permission.disabled)
      .map((permission) => permission.value);

    Object.entries(permissionsFormValue).forEach(([controlName, value]) => {
      if (!value) {
        return;
      }

      const permission = formControlsToPermissionsEnumMap[controlName];

      if (permission) {
        if (!disabledPermissions.includes(permission)) {
          permissions.granted.push(permission);
        }
      } else if (!disabledInfoPages.includes(controlName)) {
        permissions.informationPages.push(controlName);
      }
    });

    return permissions;
  }
}
